import { useState, useRef, KeyboardEvent, useEffect } from 'react';
import { Send, Smile, Image, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { EmojiPicker } from './EmojiPicker';
import { useChatContext } from '../../contexts/ChatContext';
import { useChatStore } from '../../stores/chatStore';
import { useAuth } from '../../contexts/AuthContext';
import { mediaService } from '../../services/mediaService';

interface MessageInputProps {
    onSend: (content: string) => void;
    disabled?: boolean;
}

/**
 * Message input - Text area with send button
 * Enter to send, Shift+Enter for new line
 */
export function MessageInput({ onSend, disabled = false }: MessageInputProps) {
    const [content, setContent] = useState('');
    const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const lastTypingSentRef = useRef<number>(0);
    const { sendTyping } = useChatContext();
    const { activeConversation } = useChatStore();
    const { sk, loginMethod } = useAuth();
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const MAX_LENGTH = 2000;

    const handleSend = () => {
        const trimmed = content.trim();
        if (!trimmed || disabled) return;

        onSend(trimmed);
        setContent('');
        setIsEmojiPickerOpen(false);

        // Clear typing status immediately
        if (activeConversation) {
            sendTyping(activeConversation, false);
        }
        if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
            typingTimeoutRef.current = null;
        }

        // Reset textarea height
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
        }
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const val = e.target.value;
        setContent(val);

        // Broadcast typing status (Throttled to once every 5 seconds)
        if (activeConversation && val.trim()) {
            const now = Date.now();
            if (now - lastTypingSentRef.current > 5000) {
                sendTyping(activeConversation, true);
                lastTypingSentRef.current = now;
            }

            if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
            typingTimeoutRef.current = setTimeout(() => {
                sendTyping(activeConversation, false);
                typingTimeoutRef.current = null;
                lastTypingSentRef.current = 0; // Reset so next keystroke sends immediately
            }, 3000);
        }

        // Auto-resize
        e.target.style.height = 'auto';
        e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
    };

    const handleEmojiSelect = (emoji: string) => {
        if (!textareaRef.current) return;

        const start = textareaRef.current.selectionStart;
        const end = textareaRef.current.selectionEnd;
        const newContent = content.substring(0, start) + emoji + content.substring(end);

        setContent(newContent);

        // Focus back to textarea and move cursor
        setTimeout(() => {
            if (textareaRef.current) {
                textareaRef.current.focus();
                const newPos = start + emoji.length;
                textareaRef.current.setSelectionRange(newPos, newPos);

                // Trigger auto-resize after inserting emoji
                textareaRef.current.style.height = 'auto';
                textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
            }
        }, 10);
    };

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !loginMethod) return;

        // Reset input
        e.target.value = '';

        // 10MB limit
        if (file.size > 10 * 1024 * 1024) {
            alert('Dosya boyutu çok büyük (Maksimum 10MB).');
            return;
        }

        setIsUploading(true);
        try {
            const url = await mediaService.uploadFile(file, loginMethod, sk || undefined);

            // Send immediately
            onSend(url);

            // Clear picker and focus textarea
            setIsEmojiPickerOpen(false);
            setTimeout(() => textareaRef.current?.focus(), 50);
        } catch (error: any) {
            console.error('Image upload failed:', error);
            alert(error.message || 'Resim yüklenemedi. Lütfen tekrar deneyin.');
        } finally {
            setIsUploading(false);
        }
    };

    // Cleanup typing timeout on unmount
    useEffect(() => {
        return () => {
            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
            }
        };
    }, []);

    return (
        <div className="p-4 border-t border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-900 relative">
            {/* Privacy indicator */}
            <div className="flex items-center gap-1 mb-2 text-xs text-gray-500 dark:text-gray-400">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M5.338 1.59a61.44 61.44 0 0 0-2.837.856.481.481 0 0 0-.328.39c-.554 4.157.726 7.19 2.253 9.188a10.725 10.725 0 0 0 2.287 2.233c.346.244.652.42.893.533.12.057.218.095.293.118a.55.55 0 0 0 .101.025.615.615 0 0 0 .1-.025c.076-.023.174-.061.294-.118.24-.113.547-.29.893-.533a10.726 10.726 0 0 0 2.287-2.233c1.527-1.997 2.807-5.031 2.253-9.188a.48.48 0 0 0-.328-.39c-.651-.213-1.75-.56-2.837-.855C9.552 1.29 8.531 1.067 8 1.067c-.53 0-1.552.223-2.662.524zM5.072.56C6.157.265 7.31 0 8 0s1.843.265 2.928.56c1.11.3 2.229.655 2.887.87a1.54 1.54 0 0 1 1.044 1.262c.596 4.477-.787 7.795-2.465 9.99a11.775 11.775 0 0 1-2.517 2.453 7.159 7.159 0 0 1-1.048.625c-.28.132-.581.24-.829.24s-.548-.108-.829-.24a7.158 7.158 0 0 1-1.048-.625 11.777 11.777 0 0 1-2.517-2.453C1.928 10.487.545 7.169 1.141 2.692A1.54 1.54 0 0 1 2.185 1.43 62.456 62.456 0 0 1 5.072.56z" />
                </svg>
                <span>Uçtan uca şifreli • Sadece ikiniz arasında</span>
            </div>

            {/* Input Container */}
            <div className="flex items-end gap-2 relative">
                <div className="flex-1 relative flex items-end bg-white dark:bg-surface-800 border border-surface-300 dark:border-surface-600 rounded-xl focus-within:ring-2 focus-within:ring-mavi-500 transition-all">
                    <textarea
                        ref={textareaRef}
                        value={content}
                        onChange={handleInput}
                        onKeyDown={handleKeyDown}
                        placeholder="Mesaj yazın..."
                        disabled={disabled}
                        maxLength={MAX_LENGTH}
                        rows={1}
                        className="flex-1 px-4 py-2 bg-transparent font-merriweather text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 resize-none focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                    />

                    <div className="flex items-center pr-2 pb-1.5 gap-0.5">
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleImageUpload}
                            accept="image/*"
                            className="hidden"
                        />
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={disabled || isUploading}
                            className={`p-1.5 rounded-lg transition-colors text-gray-400 hover:text-mavi-500 hover:bg-mavi-50 dark:hover:bg-mavi-900/30 disabled:opacity-50`}
                            title="Resim ekle"
                        >
                            {isUploading ? (
                                <Loader2 className="w-5 h-5 animate-spin" />
                            ) : (
                                <Image className="w-5 h-5" />
                            )}
                        </button>
                        <button
                            type="button"
                            onClick={() => setIsEmojiPickerOpen(!isEmojiPickerOpen)}
                            disabled={disabled}
                            className={`p-1.5 rounded-lg transition-colors ${isEmojiPickerOpen
                                ? 'text-mavi-500 bg-mavi-50 dark:bg-mavi-900/30'
                                : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-surface-100 dark:hover:bg-surface-700'
                                }`}
                            title="Emoji ekle"
                        >
                            <Smile className="w-5 h-5" />
                        </button>
                    </div>

                    <EmojiPicker
                        isOpen={isEmojiPickerOpen}
                        onClose={() => setIsEmojiPickerOpen(false)}
                        onSelect={handleEmojiSelect}
                    />
                </div>

                <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleSend}
                    disabled={!content.trim() || disabled}
                    className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-mavi-500 to-mavi-600 text-white rounded-xl shadow-lg shadow-mavi-500/30 hover:shadow-mavi-500/50 transition-shadow disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
                    aria-label="Gönder"
                >
                    <Send className="w-5 h-5" />
                </motion.button>
            </div>

            {/* Character count */}
            {content.length > MAX_LENGTH * 0.9 && (
                <div className="mt-1 text-xs text-right text-gray-500 dark:text-gray-400">
                    {content.length}/{MAX_LENGTH}
                </div>
            )}
        </div>
    );
}
